import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, FieldLabel } from "@/components/ui/field";
import { ChevronRight, ChevronDown, Plus, Pencil, Ban } from "lucide-react";
import { useAccountTree, useAccounts, useCreateAccount, useUpdateAccount, useDeactivateAccount } from "../hooks/useAccounting";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { useNotification } from "@/shared/hooks/useNotification";
import { useCurrency } from "@/shared/hooks/useCurrency";
import { getErrorMessage, getFieldErrors } from "@/shared/utils/errorHandler";
import { accountingApi } from "../services/accountingApi";
import type { AccountTreeDto, CreateAccountDto, UpdateAccountDto } from "../services/accountingApi";

const ACCOUNT_TYPES = [
  { value: "1", label: "Asset" },
  { value: "2", label: "Liability" },
  { value: "3", label: "Equity" },
  { value: "4", label: "Income" },
  { value: "5", label: "Expense" },
];

function AccountTypeColor({ typeName }: { typeName: string }) {
  const colors: Record<string, string> = {
    Asset: "bg-blue-100 text-blue-800",
    Liability: "bg-red-100 text-red-800",
    Equity: "bg-purple-100 text-purple-800",
    Income: "bg-green-100 text-green-800",
    Expense: "bg-orange-100 text-orange-800",
  };
  return <Badge className={colors[typeName] || "bg-gray-100 text-gray-800"}>{typeName}</Badge>;
}

// ── Tree Node Component ────────────────────────────────────────────────────────

function TreeNode({
  node,
  level,
  onEdit,
  onDeactivate,
  onAddChild,
  expandedIds,
  onToggle,
}: {
  node: AccountTreeDto;
  level: number;
  onEdit: (node: AccountTreeDto) => void;
  onDeactivate: (id: string) => void;
  onAddChild: (parentId: string, parentType: number) => void;
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
}) {
  const hasChildren = node.children && node.children.length > 0;
  const expanded = expandedIds.has(node.id);
  const { format: formatCurrency } = useCurrency();

  return (
    <div>
      <div
        className={`grid grid-cols-[28px_minmax(80px,.8fr)_minmax(110px,1.2fr)_70px_minmax(70px,.7fr)_44px] sm:grid-cols-[32px_120px_minmax(140px,1fr)_90px_100px] md:grid-cols-[36px_150px_minmax(220px,1fr)_120px_110px_140px_120px] items-center gap-2 px-2 h-11 border-b border-border/50 hover:bg-muted/30 ${!node.isActive ? "opacity-60" : ""}`}
      >
        <button
          className="w-5 h-5 flex items-center justify-center shrink-0"
          onClick={() => onToggle(node.id)}
          aria-label={expanded ? "Collapse account group" : "Expand account group"}
        >
          {hasChildren ? (expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : <span className="w-4" />}
        </button>

        <span className="font-mono text-xs md:text-sm text-muted-foreground truncate">{node.accountCode}</span>
        <div className="min-w-0" style={{ paddingLeft: `${level * 14}px` }}>
          <span className={`block text-sm truncate ${node.isGroup ? "font-semibold" : "font-normal"}`}>{node.accountName}</span>
        </div>
        <div className="shrink-0"><AccountTypeColor typeName={node.accountTypeName} /></div>

        {node.isGroup ? (
          <Badge variant="outline" className="text-xs shrink-0 hidden md:inline-flex">Group</Badge>
        ) : (
          <Badge variant="secondary" className="text-xs shrink-0 hidden md:inline-flex">Posting</Badge>
        )}

        <span className="text-xs sm:text-sm font-mono text-right shrink-0 truncate">{formatCurrency(node.balance)}</span>

        <div className="flex gap-1 shrink-0 justify-end">
          <PermissionGate permissions={["accounts.create"]}>
            {node.isGroup && (
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); onAddChild(node.id, node.accountType); }}>
                <Plus className="h-3.5 w-3.5" />
              </Button>
            )}
          </PermissionGate>
          <PermissionGate permissions={["accounts.update"]}>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); onEdit(node); }}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </PermissionGate>
          <PermissionGate permissions={["accounts.deactivate"]}>
            {node.isActive && (
              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={(e) => { e.stopPropagation(); onDeactivate(node.id); }}>
                <Ban className="h-3.5 w-3.5" />
              </Button>
            )}
          </PermissionGate>
        </div>
      </div>

      {expanded && hasChildren && (
        <div className="ml-4 border-l border-border/50">
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              expandedIds={expandedIds}
              onToggle={onToggle}
              onEdit={onEdit}
              onDeactivate={onDeactivate}
              onAddChild={onAddChild}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export function ChartOfAccountsPage() {
  const { data: tree, isLoading } = useAccountTree();
  const { data: allAccounts } = useAccounts();
  const createMutation = useCreateAccount();
  const updateMutation = useUpdateAccount();
  const deactivateMutation = useDeactivateAccount();
  const notify = useNotification();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [search, setSearch] = useState("");
  const [accountTypeFilter, setAccountTypeFilter] = useState("all");
  const [postingFilter, setPostingFilter] = useState("all");
  const [showInactive, setShowInactive] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const [form, setForm] = useState<{
    id?: string;
    accountCode: string;
    accountName: string;
    accountType: string;
    parentAccountId: string;
    description: string;
    isGroup: boolean;
  }>({
    accountCode: "",
    accountName: "",
    accountType: "1",
    parentAccountId: "",
    description: "",
    isGroup: false,
  });

  const resetForm = () => {
    setForm({ accountCode: "", accountName: "", accountType: "1", parentAccountId: "", description: "", isGroup: false });
    setFieldErrors({});
    setEditMode(false);
  };

  const openCreate = (parentId?: string, parentType?: number) => {
    resetForm();
    if (parentId) {
      setForm((f) => ({ ...f, parentAccountId: parentId, accountType: String(parentType || 1) }));
    }
    setDialogOpen(true);
  };

  const openEdit = async (node: AccountTreeDto) => {
    const detail = await accountingApi.getAccountById(node.id).catch(() => null);
    setForm({
      id: node.id,
      accountCode: node.accountCode,
      accountName: node.accountName,
      accountType: String(node.accountType),
      parentAccountId: detail?.parentAccountId ?? "",
      description: detail?.description ?? "",
      isGroup: node.isGroup,
    });
    setEditMode(true);
    setFieldErrors({});
    setDialogOpen(true);
  };

  const handleDeactivate = (id: string) => {
    deactivateMutation.mutate(id, {
      onSuccess: () => notify.success("Account deactivated"),
      onError: (err) => notify.error(getErrorMessage(err)),
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    if (editMode && form.id) {
      const dto: UpdateAccountDto = {
        id: form.id,
        accountName: form.accountName,
        description: form.description || null,
        parentAccountId: form.parentAccountId || null,
        isGroup: form.isGroup,
      };
      updateMutation.mutate({ id: form.id, dto }, {
        onSuccess: () => { notify.success("Account updated"); setDialogOpen(false); },
        onError: (err) => {
          const fe = getFieldErrors(err);
          if (fe) setFieldErrors(fe);
          notify.error(getErrorMessage(err));
        },
      });
    } else {
      const dto: CreateAccountDto = {
        accountCode: form.accountCode,
        accountName: form.accountName,
        accountType: Number(form.accountType),
        parentAccountId: form.parentAccountId || null,
        description: form.description || null,
        isGroup: form.isGroup,
      };
      createMutation.mutate(dto, {
        onSuccess: () => { notify.success("Account created"); setDialogOpen(false); resetForm(); },
        onError: (err) => {
          const fe = getFieldErrors(err);
          if (fe) setFieldErrors(fe);
          notify.error(getErrorMessage(err));
        },
      });
    }
  };

  // Filter tree by search + finance controls
  const filterTree = (nodes: AccountTreeDto[], term: string): AccountTreeDto[] => {
    return nodes
      .map((n) => {
        const filteredChildren = filterTree(n.children || [], term);

        const matchesSearch =
          n.accountCode.toLowerCase().includes(term.toLowerCase()) ||
          n.accountName.toLowerCase().includes(term.toLowerCase());

        const matchesType = accountTypeFilter === "all" || String(n.accountType) === accountTypeFilter;
        const matchesPosting = postingFilter === "all" || (postingFilter === "group" ? n.isGroup : !n.isGroup);
        const matchesActive = showInactive || n.isActive;

        const matches = matchesSearch && matchesType && matchesPosting && matchesActive;

        if (matches || filteredChildren.length > 0) {
          return { ...n, children: filteredChildren };
        }
        return null;
      })
      .filter(Boolean) as AccountTreeDto[];
  };

  const filteredTree = tree ? filterTree(tree, search.trim()) : [];

  const allExpandableIds = useMemo(() => {
    const ids: string[] = [];
    const walk = (nodes: AccountTreeDto[]) => {
      for (const n of nodes) {
        if ((n.children?.length ?? 0) > 0) ids.push(n.id);
        if (n.children?.length) walk(n.children);
      }
    };
    walk(tree ?? []);
    return ids;
  }, [tree]);

  useEffect(() => {
    if (!tree) return;
    const initial = new Set<string>();
    for (const n of tree) {
      if ((n.children?.length ?? 0) > 0) initial.add(n.id);
    }
    setExpandedIds(initial);
  }, [tree]);

  useEffect(() => {
    if (!search.trim()) return;
    setExpandedIds(new Set(allExpandableIds));
  }, [search, allExpandableIds]);

  const toggleNode = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpandedIds(new Set(allExpandableIds));
  const collapseAll = () => setExpandedIds(new Set());

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Chart of Accounts</h1>
          <p className="text-muted-foreground">Manage your general ledger account structure</p>
        </div>
        <PermissionGate permissions={["accounts.create"]}>
          <Button onClick={() => openCreate()}>
            <Plus className="h-4 w-4 mr-2" /> New Account
          </Button>
        </PermissionGate>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder="Search accounts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full md:w-72"
            />

            <Select value={accountTypeFilter} onValueChange={setAccountTypeFilter}>
              <SelectTrigger className="w-full sm:w-[150px]"><SelectValue placeholder="Account type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {ACCOUNT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={postingFilter} onValueChange={setPostingFilter}>
              <SelectTrigger className="w-full sm:w-[150px]"><SelectValue placeholder="Group/Posting" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Rows</SelectItem>
                <SelectItem value="group">Groups Only</SelectItem>
                <SelectItem value="posting">Posting Only</SelectItem>
              </SelectContent>
            </Select>

            <label className="inline-flex items-center gap-2 text-sm text-muted-foreground select-none">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              Show inactive
            </label>

            <Button variant="outline" size="sm" onClick={expandAll}>Expand All</Button>
            <Button variant="outline" size="sm" onClick={collapseAll}>Collapse All</Button>

            <div className="flex gap-2 ml-auto text-sm text-muted-foreground">
              <span>{countTreeNodes(filteredTree)} shown</span>
              {tree && <span>of {tree.reduce((acc, n) => acc + countNodes(n), 0)} accounts</span>}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading chart of accounts...</div>
          ) : filteredTree.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No accounts found</div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <div className="min-w-[560px] md:min-w-[860px]">
                <div className="grid grid-cols-[28px_minmax(80px,.8fr)_minmax(110px,1.2fr)_70px_minmax(70px,.7fr)_44px] sm:grid-cols-[32px_120px_minmax(140px,1fr)_90px_100px] md:grid-cols-[36px_150px_minmax(220px,1fr)_120px_110px_140px_120px] items-center h-10 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b bg-muted/40 sticky top-0 z-10">
                  <span></span>
                  <span>Code</span>
                  <span>Name</span>
                  <span>Type</span>
                  <span className="hidden sm:block">Group/Posting</span>
                  <span className="text-right">Balance</span>
                  <span className="text-right">Actions</span>
                </div>
                {filteredTree.map((node) => (
                  <TreeNode
                    key={node.id}
                    node={node}
                    level={0}
                    expandedIds={expandedIds}
                    onToggle={toggleNode}
                    onEdit={openEdit}
                    onDeactivate={handleDeactivate}
                    onAddChild={openCreate}
                  />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editMode ? "Edit Account" : "New Account"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editMode && (
              <Field>
                <FieldLabel htmlFor="accountCode">Account Code <span className="text-red-500">*</span></FieldLabel>
                <Input id="accountCode" value={form.accountCode} onChange={(e) => setForm({ ...form, accountCode: e.target.value })} required />
                {fieldErrors.accountCode && <p className="text-red-500 text-xs mt-1">{fieldErrors.accountCode.join(", ")}</p>}
              </Field>
            )}
            <Field>
              <FieldLabel htmlFor="accountName">Account Name <span className="text-red-500">*</span></FieldLabel>
              <Input id="accountName" value={form.accountName} onChange={(e) => setForm({ ...form, accountName: e.target.value })} required />
              {fieldErrors.accountName && <p className="text-red-500 text-xs mt-1">{fieldErrors.accountName.join(", ")}</p>}
            </Field>
            {!editMode && (
              <Field>
                <FieldLabel>Account Type <span className="text-red-500">*</span></FieldLabel>
                <Select value={form.accountType} onValueChange={(v) => setForm({ ...form, accountType: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACCOUNT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field>
              <FieldLabel>Parent Account</FieldLabel>
              <Select value={form.parentAccountId || "none"} onValueChange={(v) => setForm({ ...form, parentAccountId: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Root (no parent)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Root (no parent)</SelectItem>
                  {allAccounts?.filter((a) => a.isGroup && a.isActive).map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.accountCode} — {a.accountName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="description">Description</FieldLabel>
              <Input id="description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="isGroup" checked={form.isGroup} onChange={(e) => setForm({ ...form, isGroup: e.target.checked })} />
              <label htmlFor="isGroup" className="text-sm">This is a group header (non-postable)</label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending) ? "Saving..." : editMode ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function countNodes(node: AccountTreeDto): number {
  return 1 + (node.children || []).reduce((acc, c) => acc + countNodes(c), 0);
}

function countTreeNodes(nodes: AccountTreeDto[]): number {
  return nodes.reduce((acc, n) => acc + countNodes(n), 0);
}
